import { db } from '../firebase';
import { auditService } from './auditService';
import {
    collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
    query, where
} from 'firebase/firestore';

const COLLECTION_PERMITS = 'permits';

// ─────────────────────────────────────────────────────────────────────────
//  BEREIK: welke locaties mag deze gebruiker zien?
//
//  De beveiligingsregels weigeren een query die ook maar één document raakt
//  dat de gebruiker niet mag lezen. Regels filteren niet, ze weigeren. Een
//  query over de hele collectie werkt dus alleen nog voor de superuser; alle
//  andere gebruikers moeten vooraf op hun eigen locaties filteren.
//
//  null  = geen filter, de hele collectie (alleen zinvol voor de superuser)
//  []    = geen enkele locatie, levert altijd een lege lijst op
//  [...] = precies deze locaties
// ─────────────────────────────────────────────────────────────────────────
export function permitScope(user) {
    if (!user) return [];
    return user.role === 'superuser' ? null : (user.allowedLocations || []);
}

// Voert dezelfde query één keer per locatie uit en voegt de uitkomsten samen.
// Eén 'in'-query zou ook kunnen, maar de regels moeten dan elke waarde
// afzonderlijk kunnen bewijzen en dat is broos; losse queries zijn dat niet.
async function fetchBinnenBereik(scope, extraConstraints = []) {
    const col = collection(db, COLLECTION_PERMITS);
    if (scope === null) {
        const snapshot = await getDocs(extraConstraints.length ? query(col, ...extraConstraints) : col);
        return snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
    }
    if (scope.length === 0) return [];
    const snapshots = await Promise.all(scope.map(loc =>
        getDocs(query(col, where('location', '==', loc), ...extraConstraints))
    ));
    return snapshots.flatMap(s => s.docs.map(d => ({ ...d.data(), id: d.id })));
}

export const permitService = {
    async savePermit(permitData) {
        try {
            const docRef = permitData.id ? doc(db, COLLECTION_PERMITS, permitData.id) : doc(collection(db, COLLECTION_PERMITS));

            const finalData = {
                ...permitData,
                id: docRef.id,
                status: permitData.status || 'pending',
                createdAt: permitData.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            const sanitizedData = JSON.parse(JSON.stringify(finalData));
            await setDoc(docRef, sanitizedData, { merge: true });
            return finalData.id;
        } catch (error) {
            console.error('[PermitService] savePermit failed:', error);
            try { auditService.logAction(permitData?.applicantId || 'unknown', 'PERMIT_SAVE_ERROR', { error: error.message }, permitData?.location || null); } catch (_) { }
            throw error;
        }
    },

    // scope komt uit permitScope(currentUser); zonder scope is dit alleen
    // nog bruikbaar voor de superuser.
    async getPermitsByStatus(status, scope = null) {
        try {
            const permits = await fetchBinnenBereik(scope, [where("status", "==", status)]);
            return permits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } catch (error) {
            console.error('[PermitService] getPermitsByStatus failed:', error);
            try { auditService.logAction('system', 'PERMIT_QUERY_ERROR', { method: 'getPermitsByStatus', error: error.message }); } catch (_) { }
            throw error;
        }
    },

    async getPermitsByApplicant(uid) {
        try {
            const q = query(collection(db, COLLECTION_PERMITS), where("applicantId", "==", uid));
            const querySnapshot = await getDocs(q);
            const permits = [];
            querySnapshot.forEach((doc) => {
                permits.push({ ...doc.data(), id: doc.id });
            });
            return permits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } catch (error) {
            console.error('[PermitService] getPermitsByApplicant failed:', error);
            try { auditService.logAction('system', 'PERMIT_QUERY_ERROR', { method: 'getPermitsByApplicant', error: error.message }); } catch (_) { }
            throw error;
        }
    },

    async getAllPermits(scope = null) {
        try {
            const permits = await fetchBinnenBereik(scope);
            return permits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } catch (error) {
            console.error('[PermitService] getAllPermits failed:', error);
            try { auditService.logAction('system', 'PERMIT_QUERY_ERROR', { method: 'getAllPermits', error: error.message }); } catch (_) { }
            throw error;
        }
    },

    async getAllAssignedPermits() {
        try {
            const allPermits = await this.getAllPermits();
            return allPermits.filter(p => p.assignedTo);
        } catch (error) {
            console.error('[PermitService] getAllAssignedPermits failed:', error);
            throw error;
        }
    },

    async updatePermitStatus(permitId, newStatus, authorName, authorId) {
        // Zie deletePermit: een statuswissel hoort met locatie in het logboek,
        // anders is hij straks alleen voor de superuser zichtbaar.
        let locationId = null;
        try {
            const permitRef = doc(db, COLLECTION_PERMITS, permitId);
            try {
                const snap = await getDoc(permitRef);
                if (snap.exists()) locationId = snap.data().location || null;
            } catch (_) { /* lezen mag mislukken, de wijziging gaat door */ }
            await updateDoc(permitRef, {
                status: newStatus,
                updatedAt: new Date().toISOString(),
                lastAuthor: authorName,
                lastAuthorId: authorId
            });
            auditService.logAction(authorId, 'PERMIT_STATUS_CHANGE', { permitId, newStatus }, locationId);
        } catch (error) {
            console.error('[PermitService] updatePermitStatus failed:', error);
            try { auditService.logAction(authorId || 'unknown', 'PERMIT_STATUS_ERROR', { permitId, error: error.message }, locationId); } catch (_) { }
            throw error;
        }
    },

    async assignPermit(id, userId, feedback = '') {
        try {
            const docRef = doc(db, COLLECTION_PERMITS, id);
            await updateDoc(docRef, {
                assignedTo: userId,
                assignmentFeedback: feedback,
                assignedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('[PermitService] assignPermit failed:', error);
            try { auditService.logAction('system', 'PERMIT_ASSIGN_ERROR', { permitId: id, error: error.message }); } catch (_) { }
            throw error;
        }
    },

    async updatePermitChecklist(id, checklistData) {
        try {
            const docRef = doc(db, COLLECTION_PERMITS, id);
            await updateDoc(docRef, {
                executionChecklist: checklistData,
                lastExecutionUpdate: new Date().toISOString()
            });
        } catch (error) {
            console.error('[PermitService] updatePermitChecklist failed:', error);
            throw error;
        }
    },

    async saveLotoMetadata(id, metadata) {
        try {
            const docRef = doc(db, COLLECTION_PERMITS, id);
            await setDoc(docRef, {
                lotoMetadata: metadata,
                updatedAt: new Date().toISOString()
            }, { merge: true });
        } catch (error) {
            console.error('[PermitService] saveLotoMetadata failed:', error);
            throw error;
        }
    },

    async getPermitsForUser(userIds) {
        try {
            const ids = Array.isArray(userIds) ? userIds : [userIds];
            const validIds = ids.filter(id => id);
            if (validIds.length === 0) return [];

            const q = query(collection(db, COLLECTION_PERMITS), where("assignedTo", "in", validIds));
            const querySnapshot = await getDocs(q);
            const permits = [];
            querySnapshot.forEach((doc) => {
                permits.push({ ...doc.data(), id: doc.id });
            });
            return permits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } catch (error) {
            console.error('[PermitService] getPermitsForUser failed:', error);
            throw error;
        }
    },

    async deletePermit(permitId, actorId = 'unknown') {
        // Eerst lezen, dan verwijderen. Niet alleen voor de logregel: een
        // verwijdering zonder locatie in het logboek is straks alleen voor
        // de superuser zichtbaar, en juist verwijderingen wil een beheerder
        // in zijn eigen auditoverzicht terugzien.
        let locationId = null;
        try {
            const permitRef = doc(db, COLLECTION_PERMITS, permitId);
            try {
                const snap = await getDoc(permitRef);
                if (snap.exists()) locationId = snap.data().location || null;
            } catch (_) { /* lezen mag mislukken, verwijderen gaat door */ }
            await deleteDoc(permitRef);
            auditService.logAction(actorId, 'PERMIT_DELETE', { permitId }, locationId);
        } catch (error) {
            console.error('[PermitService] deletePermit failed:', error);
            try { auditService.logAction(actorId, 'PERMIT_DELETE_ERROR', { permitId, error: error.message }, locationId); } catch (_) { }
            throw error;
        }
    },

    async deletePermitsByTaskImage(taskName, imageUrl) {
        try {
            const permitsRef = collection(db, COLLECTION_PERMITS);
            const docsToDelete = new Map();

            const queries = [
                query(permitsRef, where("taskImageUrl", "==", imageUrl)),
                query(permitsRef, where("taskImageUrls", "array-contains", imageUrl)),
                query(permitsRef, where("lotoPhotos", "array-contains", imageUrl))
            ];

            const snapshots = await Promise.all(queries.map(q => getDocs(q)));

            snapshots.forEach(snap => {
                snap.forEach(doc => docsToDelete.set(doc.id, doc.ref));
            });

            if (docsToDelete.size === 0) return 0;

            await Promise.all(Array.from(docsToDelete.values()).map(ref => deleteDoc(ref)));
            return docsToDelete.size;
        } catch (error) {
            console.error('[PermitService] deletePermitsByTaskImage failed:', error);
            throw error;
        }
    },

    async getPermitsByComponent(location, componentName) {
        if (!componentName) return [];
        try {
            const permitsRef = collection(db, COLLECTION_PERMITS);
            const q = query(
                permitsRef,
                where("component", "==", componentName),
                where("status", "in", ["pending", "approved", "active"])
            );

            const querySnapshot = await getDocs(q);
            const permits = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                if (!location || data.location === location || !data.location) {
                    permits.push({ id: doc.id, ...data });
                }
            });
            return permits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } catch (error) {
            console.error('[PermitService] getPermitsByComponent failed:', error);
            return [];
        }
    },

    async getPermitsByInstance(location, hall, line, subline, instanceName) {
        if (!location || !instanceName) return [];
        try {
            const q = query(collection(db, COLLECTION_PERMITS), where("location", "==", location));
            const querySnapshot = await getDocs(q);
            const permits = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                if (data.hall === hall &&
                    data.line === line &&
                    data.subline === subline &&
                    data.instanceName === instanceName) {
                    permits.push({ ...data, id: doc.id });
                }
            });
            return permits;
        } catch (error) {
            console.error('[PermitService] getPermitsByInstance failed:', error);
            throw error;
        }
    },

    async updatePermitAssignment(permitId, userId, userName, userEmail) {
        try {
            const permitRef = doc(db, COLLECTION_PERMITS, permitId);
            await updateDoc(permitRef, {
                assignedTo: userId,
                executor: userName,
                executorEmail: userEmail || null,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('[PermitService] updatePermitAssignment failed:', error);
            throw error;
        }
    },

    async getLastApprovedLotoPhotos(hall, line, subline, component, task, taskPhotoUrl = null) {
        try {
            const approvedPermits = await this.getPermitsByStatus('approved');
            const match = approvedPermits.find(p => {
                const hierarchyMatch =
                    p.hall === hall &&
                    p.line === line &&
                    p.subline === subline &&
                    p.component === component &&
                    p.task === task &&
                    p.lotoPhotos && p.lotoPhotos.length > 0;

                if (!hierarchyMatch) return false;

                if (taskPhotoUrl) {
                    const pPhotos = p.taskImageUrls || (p.taskImageUrl ? [p.taskImageUrl] : []);
                    const currentPhotos = Array.isArray(taskPhotoUrl) ? taskPhotoUrl : [taskPhotoUrl];
                    if (currentPhotos.length === 0) return true;
                    return currentPhotos.every(url => pPhotos.includes(url));
                }
                return true;
            });
            return match ? match.lotoPhotos : [];
        } catch (error) {
            console.error('[PermitService] getLastApprovedLotoPhotos failed:', error);
            return [];
        }
    },

    async checkIfApprovedPatternExists(permitData) {
        try {
            const permitsRef = collection(db, COLLECTION_PERMITS);
            const q = query(
                permitsRef,
                where("status", "==", "approved"),
                where("task", "==", permitData.task)
            );

            const querySnapshot = await getDocs(q);
            let matchFound = false;

            querySnapshot.forEach((doc) => {
                if (matchFound) return;
                const p = doc.data();

                const hierarchyMatch =
                    p.hall === permitData.hall &&
                    p.line === permitData.line &&
                    p.subline === permitData.subline &&
                    p.component === permitData.component;

                if (!hierarchyMatch) return;

                const pPhotos = p.taskImageUrls || (p.taskImageUrl ? [p.taskImageUrl] : []);
                const currentPhotos = permitData.taskImageUrls || (permitData.taskImageUrl ? [permitData.taskImageUrl] : []);

                if (currentPhotos.length === 0) return;
                const photosMatch = currentPhotos.every(url => pPhotos.includes(url));
                if (!photosMatch) return;

                const currentSopUrl = permitData.selectedSop?.url || permitData.sopLink;
                const approvedSopUrl = p.selectedSop?.url || p.sopLink;
                if (currentSopUrl !== approvedSopUrl) return;

                matchFound = true;
            });

            return matchFound;
        } catch (error) {
            console.error('[PermitService] checkIfApprovedPatternExists failed:', error);
            return false;
        }
    },

    // --- Active LOTO Dashboard Features (V5.5) ---

    async getActiveLOTOs() {
        try {
            const permitsRef = collection(db, COLLECTION_PERMITS);
            const q = query(permitsRef, where("status", "in", ["active", "paused"]));

            const querySnapshot = await getDocs(q);
            const permits = [];
            querySnapshot.forEach((doc) => {
                permits.push({ id: doc.id, ...doc.data() });
            });
            return permits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } catch (error) {
            console.error('[PermitService] getActiveLOTOs failed:', error);
            return [];
        }
    },

    async forceUnlockPermit(id, user, reason) {
        if (!id || !user || !reason) throw new Error("Missing params for force unlock");

        try {
            const docRef = doc(db, COLLECTION_PERMITS, id);
            const snap = await getDoc(docRef);
            const currentData = snap.exists() ? snap.data() : {};

            const auditEntry = {
                executedBy: user.uid,
                executedByName: user.username || user.email,
                role: user.role,
                reason: reason,
                timestamp: new Date().toISOString(),
                previousStatus: currentData.status
            };

            await updateDoc(docRef, {
                status: 'terminated',
                forceUnlockAudit: auditEntry,
                terminationReason: reason,
                updatedAt: new Date().toISOString(),
                endedAt: new Date().toISOString()
            });

            return auditEntry;
        } catch (error) {
            console.error('[PermitService] forceUnlockPermit failed:', error);
            try { auditService.logAction(user?.uid || 'unknown', 'FORCE_UNLOCK_ERROR', { permitId: id, error: error.message }); } catch (_) { }
            throw error;
        }
    },

    async updatePermitsImageReference(oldUrl, newUrl) {
        try {
            const permitsRef = collection(db, COLLECTION_PERMITS);

            const q1 = query(permitsRef, where("taskImageUrl", "==", oldUrl));
            const q2 = query(permitsRef, where("taskImageUrls", "array-contains", oldUrl));

            const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

            const docsToUpdate = new Map();
            snap1.forEach(doc => docsToUpdate.set(doc.id, doc));
            snap2.forEach(doc => docsToUpdate.set(doc.id, doc));

            const updates = [];
            docsToUpdate.forEach(docSnap => {
                const data = docSnap.data();
                const updateData = {};

                if (data.taskImageUrl === oldUrl) {
                    updateData.taskImageUrl = newUrl;
                }

                if (data.taskImageUrls && Array.isArray(data.taskImageUrls)) {
                    updateData.taskImageUrls = data.taskImageUrls.map(u => u === oldUrl ? newUrl : u);
                }

                if (Object.keys(updateData).length > 0) {
                    updates.push(updateDoc(docSnap.ref, updateData));
                }
            });

            await Promise.all(updates);
            return updates.length;
        } catch (error) {
            console.error('[PermitService] updatePermitsImageReference failed:', error);
            throw error;
        }
    }
};
